import React from 'react';
import { LoadingSpinner } from '@/components';
import './AnalyticsTable.scss';

export interface TableColumn<T> {
  key: keyof T | string;
  header: string;
  render?: (item: T) => React.ReactNode;
  width?: string;
}

interface AnalyticsTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  isLoading?: boolean;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
}

function AnalyticsTable<T extends Record<string, any>>({
  data,
  columns,
  isLoading = false,
  emptyMessage = 'No data available',
  onRowClick,
}: AnalyticsTableProps<T>) {
  if (isLoading) {
    return <LoadingSpinner size="medium" message="Loading data..." />;
  }

  if (data.length === 0) {
    return (
      <div className="analytics-table-empty">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="analytics-table-container">
      <table className="analytics-table">
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th key={index} style={{ width: column.width }}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, rowIndex) => (
            <tr
              key={rowIndex}
              onClick={() => onRowClick && onRowClick(item)}
              className={onRowClick ? 'clickable' : ''}
            >
              {columns.map((column, colIndex) => (
                <td key={colIndex}>
                  {column.render
                    ? column.render(item)
                    : item[column.key as keyof T] || '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AnalyticsTable;
